import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import VereinbarungService from '../services/vereinbarung.service';

const EMPTY = { katalog_id: null, bezeichnung: '', beschreibung: '' };

// Verwaltung des zentralen Titel-Katalogs (reine Titelbezeichnungen).
// scope='system' -> Admin pflegt zentrale System-Titel.
// scope='stage'  -> Stufe pflegt eigene Titelbezeichnungen.
// Die Stufen-Freigabe erfolgt nicht hier, sondern im Vereinbarungsmodul (Template).
const BoardTitelKatalog = ({ scope = 'stage' }) => {
    const isSystem = scope === 'system';

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [current, setCurrent] = useState({ ...EMPTY });
    const [isEditMode, setIsEditMode] = useState(false);

    const fetchList = useCallback(() => {
        setLoading(true);
        VereinbarungService.getTitleCatalog(isSystem ? 'system' : 'stage')
            .then((res) => {
                setList(Array.isArray(res.data) ? res.data : []);
                setLoading(false);
            })
            .catch(() => {
                setError('Fehler beim Laden der Titel.');
                setLoading(false);
            });
    }, [isSystem]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleClose = () => {
        setShowModal(false);
        setCurrent({ ...EMPTY });
        setIsEditMode(false);
    };

    const handleShowCreate = () => {
        setIsEditMode(false);
        setCurrent({ ...EMPTY });
        setShowModal(true);
    };

    const handleShowEdit = (entry) => {
        setIsEditMode(true);
        setCurrent({ ...EMPTY, ...entry });
        setShowModal(true);
    };

    const handleSave = () => {
        setError('');
        const payload = {
            bezeichnung: current.bezeichnung,
            beschreibung: current.beschreibung,
            ...(isSystem ? { is_system: true } : {}),
        };
        const promise = isEditMode
            ? VereinbarungService.updateTitleCatalogEntry(current.katalog_id, payload)
            : VereinbarungService.createTitleCatalogEntry(payload);

        promise
            .then(() => { fetchList(); handleClose(); })
            .catch((err) => {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Fehler beim Speichern.';
                setError(msg);
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Diesen Titel wirklich loeschen?')) return;
        VereinbarungService.deleteTitleCatalogEntry(id)
            .then(() => fetchList())
            .catch((err) => {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Fehler beim Loeschen.';
                setError(msg);
            });
    };

    return (
        <Container>
            <div className="d-flex justify-content-between align-items-center my-3 flex-wrap gap-2">
                <h1 className="mb-0">
                    {isSystem ? 'System-Vereinbarungstitel' : 'Meine Vereinbarungstitel'}
                </h1>
                <Button variant="success" onClick={handleShowCreate}>
                    + Neue Titelbezeichnung
                </Button>
            </div>

            <Alert variant="info">
                {isSystem
                    ? 'Zentrale Titelbezeichnungen, aus denen System-Vereinbarungsvorlagen ihren Titel waehlen.'
                    : 'Eigene Titelbezeichnungen dieser Stufe. Sie koennen diese beim Erstellen einer Vereinbarung als individuellen Titel auswaehlen.'}
            </Alert>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th style={{ width: '80px' }}>ID</th>
                            <th>Bezeichnung</th>
                            <th>Beschreibung</th>
                            <th style={{ width: '130px' }}>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center text-muted">
                                    Noch keine Titel angelegt.
                                </td>
                            </tr>
                        ) : list.map((entry) => (
                            <tr key={entry.katalog_id}>
                                <td>{entry.katalog_id}</td>
                                <td>{entry.bezeichnung}</td>
                                <td>{entry.beschreibung}</td>
                                <td>
                                    <Button variant="primary" size="sm" className="me-2" title="Bearbeiten" aria-label="Bearbeiten" onClick={() => handleShowEdit(entry)}>
                                        <FaEdit />
                                    </Button>
                                    <Button variant="danger" size="sm" title="Loeschen" aria-label="Loeschen" onClick={() => handleDelete(entry.katalog_id)}>
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditMode ? 'Titel bearbeiten' : 'Neue Titelbezeichnung'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Bezeichnung</Form.Label>
                            <Form.Control
                                type="text"
                                value={current.bezeichnung}
                                onChange={(e) => setCurrent({ ...current, bezeichnung: e.target.value })}
                                placeholder="z.B. Vertriebsvereinbarung"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Beschreibung (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={current.beschreibung || ''}
                                onChange={(e) => setCurrent({ ...current, beschreibung: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleSave}>Speichern</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BoardTitelKatalog;
